import { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  applyFilterAction,
  applyFiltersAction,
  clearFiltersAction,
  removeFilterAction,
} from 'src/store/slices/serviceRequestFiltersSlice';

export interface ServiceRequestFilters {
  Statuses: string[];
  selectedDate: any;
  UnitId: any[];
  TagIds: any[];
  booleanFilters: string[];
  SubmittedFrom: string | null;
  SubmittedTo: string | null;
  PropertyId: string | null;
  BuildingId: string | null;
  LevelId: string | null;
  ItemTypeId: string | null;
  AssignedToServiceProviderId?: string | null;
  ServiceType?: string | null;
  RequestType?: string | null;
  [key: string]: any;
}

interface UseServiceRequestFiltersReturn {
  filters: ServiceRequestFilters;
  formattedFiltersForApi: any;
  applyFilter: (key: string, data: any) => void;
  applyFilters: (partial: Partial<ServiceRequestFilters>) => void;
  removeFilter: (key: string) => void;
  clearFilters: () => void;
}

// Ported verbatim from the old selectFormattedFiltersForApi selector so the API
// payload shape is unchanged — just sourced from local state instead of redux.
export const formatFiltersForApi = (filters: ServiceRequestFilters): any => {
  return {
    WalkthroughPermissions: filters.booleanFilters?.filter(
      (option: string) =>
        option === 'Internal' || option === 'Homeowner or Occupant'
    ),
    WalkthroughType: filters.booleanFilters?.includes('Regular Service Requests')
      ? 'Regular Service Requests'
      : null,
    OnlyTarionServiceRequests: filters.booleanFilters?.includes('Imported')
      ? true
      : null,
    OnlyGroupedServiceRequests: filters.booleanFilters?.includes('Grouped')
      ? true
      : null,

    Statuses: filters.Statuses,
    ServiceRequestAge: filters.selectedDate ?? undefined,
    UnitId: filters.UnitId[0]?.Id ?? undefined,
    TagIds: filters.TagIds?.map((tag: any) => tag.value) ?? undefined,
    PropertyId: filters.PropertyId ?? undefined,
    BuildingId: filters.BuildingId ?? undefined,
    LevelId: filters.LevelId ?? undefined,
    AssignedToServiceProviderId: filters.AssignedToServiceProviderId ?? undefined,
    SubmittedFrom: filters.SubmittedFrom ?? undefined,
    SubmittedTo: filters.SubmittedTo ?? undefined,
    ServiceType: filters.ServiceType ?? undefined,
    RequestType: filters.RequestType ?? undefined,
    ItemTypeId: filters.ItemTypeId ?? undefined,
  };
};

export const useServiceRequestFilters = (): UseServiceRequestFiltersReturn => {
  const dispatch = useDispatch();

  const filters = useSelector(
    (state: any) => state.serviceRequestFilters
  );

  const applyFilter = (key: string, data: any): void => {
    dispatch(applyFilterAction({ key, data }));
  };

  const applyFilters = (partial: Partial<ServiceRequestFilters>): void => {
    dispatch(applyFiltersAction(partial));
  };

  const removeFilter = (key: string): void => {
    dispatch(removeFilterAction(key));
  };

  const clearFilters = (): void => {
    dispatch(clearFiltersAction());
  };

  const formattedFiltersForApi = useMemo(
    () => formatFiltersForApi(filters),
    [filters]
  );

  return {
    filters,
    formattedFiltersForApi,
    applyFilter,
    applyFilters,
    removeFilter,
    clearFilters,
  };
};

export default useServiceRequestFilters;
